import Agilite from 'agilite'
import BookingEnums from './enums'
import dayjs from 'dayjs'
import { generateTitle } from '../../lib/utils'
import { readMedicalProfessionalUsers } from '../../Admin/medical-professionals/utils/utils'
import { readPatients } from '../../Admin/patients/utils/utils'
import axios from 'axios'
import { readClinics } from '../../Admin/clinics/utils/utils'
import { readServices } from '../../Admin/services/utils/utils'
import { readVirtualServices } from '../../Admin/virutal-services/utils/utils'
import { readSystemUsersAggregate } from '../../Admin/system-users/utils/utils'
import { updateAppointmentRecord } from '../../lib/websocket-utils'

const agilite = new Agilite({
  apiServerUrl: process.env.REACT_APP_AGILITE_API_URL,
  apiKey: process.env.REACT_APP_AGILITE_API_KEY
})

export const findOneBooking = (filter, options) => {
  return new Promise((resolve, reject) => {
    let response = null
    let queryOptions = null

    ;(async () => {
      try {
        if (options) {
          queryOptions = JSON.stringify(options)
        }

        response = await agilite.Connectors.execute(
          BookingEnums.profileKeys.BOOKINGS,
          BookingEnums.routeKeys.FIND_ONE,
          {
            filter: JSON.stringify(filter),
            page: null,
            pageLimit: null,
            options: queryOptions
          }
        )
        resolve(response.data)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

export const readBookings = (filter, options, projection, page, pageLimit) => {
  return new Promise((resolve, reject) => {
    let response = null
    let queryOptions = null

    ;(async () => {
      try {
        if (options) {
          queryOptions = JSON.stringify(options)
        }

        response = await agilite.Connectors.execute(BookingEnums.profileKeys.BOOKINGS, BookingEnums.routeKeys.READ, {
          filter: JSON.stringify(filter),
          page,
          pageLimit,
          options: queryOptions,
          projection
        })
        resolve(response.data)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

export const aggregateBookings = (pipeline) => {
  return new Promise((resolve, reject) => {
    let response = null

    ;(async () => {
      try {
        response = await agilite.Connectors.execute(
          BookingEnums.profileKeys.BOOKINGS,
          BookingEnums.routeKeys.AGGREGATE,
          {
            pipeline: JSON.stringify(pipeline)
          }
        )
        resolve(response.data)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

export const getClaimableBookings = () => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        // Get current entity information for network filtering
        const state = require('../../../store').default.getState()
        const currentEntity = state.core.entity
        const currentEntityRef = currentEntity?._id

        // Fetch all claimable bookings (flexi/priority consultations that haven't been claimed)
        const filter = {
          claimable: true,
          availableForClaiming: true,
          claimedBy: null,
          isVirtualVisit: true // Only virtual consultations can be claimed across entities
        }

        // If the current entity doesn't allow outside network access,
        // only show claimable bookings from patients in the same entity
        if (currentEntity?.allowOutsideNetwork === false && currentEntityRef) {
          filter.patientEntityRef = currentEntityRef
          console.log('Entity network filtering: Only showing claimable bookings from same entity:', currentEntityRef)
        } else {
          console.log(
            'Entity network filtering: Showing claimable bookings from all entities (allowOutsideNetwork is true)'
          )
        }

        const options = {
          sort: { dateCreated: -1 } // Most recent first, priority should be handled first
        }

        const response = await readBookings(filter, options)

        // Enrich the bookings with patient information
        const enrichedBookings = await Promise.all(
          response.map(async (booking) => {
            try {
              // Get patient information
              const patientInfo = await readPatients({ _id: booking.userRef })
              booking.patientRecord = patientInfo[0] || null

              return booking
            } catch (error) {
              console.warn('Failed to get patient info for booking:', booking._id, error)
              booking.patientRecord = null
              return booking
            }
          })
        )

        resolve(enrichedBookings)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

export const createBooking = (data) => {
  return new Promise((resolve, reject) => {
    let response = null

    ;(async () => {
      try {
        response = await agilite.Connectors.execute(BookingEnums.profileKeys.BOOKINGS, BookingEnums.routeKeys.CREATE, {
          data: JSON.stringify(data)
        })
        resolve(response.data)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

export const updateBooking = (data, filter) => {
  return new Promise((resolve, reject) => {
    let response = null
    ;(async () => {
      try {
        response = await agilite.Connectors.execute(BookingEnums.profileKeys.BOOKINGS, BookingEnums.routeKeys.UPDATE, {
          filter: JSON.stringify(filter),
          data: JSON.stringify(data)
        })

        // WebSocket - Appointment Update
        updateAppointmentRecord(response.data)

        resolve(response.data)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

export const sendBill = (data) => {
  // TODO: THIS IS TEMPORARY
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        if (data.phoneNumber) {
          agilite.Connectors.execute('sms_portal', 'send', {
            data: JSON.stringify({
              messages: [
                {
                  content: `Dear ${data.firstName} ${
                    data.lastName
                  },\n\nYour medical aid has covered R${data.medicalAidAmount.toFixed(
                    2
                  )} of your visit. The remaining amount payable by you is R${data.patientAmount.toFixed(
                    2
                  )}.\n\nYou can securely settle your account by clicking the link below:\n\nhttps://callpay.co.za/pay/83674786278\n\nIf you have any questions or need assistance, please don't hesitate to contact us.\n\nThe Meta-Clinic Team`,
                  destination: data.phoneNumber
                }
              ]
            })
          })

          await axios.post(
            'https://graph.facebook.com/v21.0/395687273638794/messages',
            {
              messaging_product: 'whatsapp',
              to: data.phoneNumber,
              type: 'template',
              template: {
                name: 'test_billing_collections',
                language: {
                  code: 'en'
                },
                components: [
                  {
                    type: 'body',
                    parameters: [
                      { type: 'text', text: 'Mr' },
                      { type: 'text', text: `${data.firstName} ${data.lastName}` },
                      {
                        type: 'text',
                        text: `${(parseFloat(data.medicalAidAmount) + parseFloat(data.patientAmount)).toFixed(2)}`
                      }
                    ]
                  }
                ]
              }
            },
            {
              headers: {
                Authorization:
                  'Bearer EAAGNEH4qZCIoBOZCZASL0ZCwTQ8nljK3cVdBpF8sdC9pAZBZByCMaygQhi6pGEO1qZBZAAtT3PI6rr7R9ekQFzAVY1zRZANErH6fyeTUsq3lb3587cq3AoE4fZCD321YTpI9zwr6SmU61IdahYSih7hZATHJEhVPXQHEJsUTmHDllxqii3Wyal0OZAQpBrrvCqopxeEmZCr2LoUeS1eiC7Vp8KJ9Jdzi2ut7Qp0oyrbe197kZD',
                'Content-Type': 'application/json'
              }
            }
          )
        }

        resolve()
      } catch (e) {
        reject(e)
      }
    })()
  })
}

export const sendOTP = (otp, data, dependant, isDependant, clinicName) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        console.log('🔍 sendOTP function - data received:', {
          hasPhoneNumber: !!data.phoneNumber,
          phoneNumber: data.phoneNumber,
          hasEmail: !!data.email,
          email: data.email,
          hasMedicalProf: !!data.medicalProf,
          medicalProf: data.medicalProf,
          firstName: data.firstName,
          bookingDate: data.bookingDate,
          startTime: data.startTime,
          endTime: data.endTime,
          otp: otp,
          clinicName: clinicName,
          isDependant: isDependant,
          dependant: dependant
        })

        if (data.phoneNumber) {
          try {
            // Validate phone number format
            if (!data.phoneNumber || data.phoneNumber.length < 10) {
              throw new Error(`Invalid phone number format: ${data.phoneNumber}`)
            }

            // Validate required fields
            if (!data.firstName) {
              throw new Error('Missing firstName for SMS')
            }

            if (!clinicName) {
              throw new Error('Missing clinicName for SMS')
            }

            // Format date and time safely
            const bookingDate = dayjs(data.bookingDate).format('DD MMM YYYY')
            const timeSlot = data.startTime ? ` at ${data.startTime}` : ''

            const smsContent = `Hi ${data.firstName}.
This is your Booking Confirmation${isDependant ? ` for ${dependant.firstName}` : ''}.

Clinic: ${clinicName}
Date: ${bookingDate}${timeSlot}
OTP: ${otp}

Please arrive 15 minutes before your booking time for screening purposes.`

            const smsPayload = {
              messages: [
                {
                  content: smsContent,
                  destination: data.phoneNumber
                }
              ]
            }

            console.log('📱 Sending SMS... [NEW FORMAT v2.0]')
            console.log('📱 SMS Content:', smsContent)
            console.log('📱 SMS Payload:', JSON.stringify(smsPayload, null, 2))

            await agilite.Connectors.execute('sms_portal', 'send', {
              data: JSON.stringify(smsPayload)
            })
            console.log('✅ SMS sent successfully')
          } catch (error) {
            console.error('❌ SMS failed:', error)
            console.error('❌ SMS Error Details:', {
              message: error.message,
              status: error.response?.status,
              statusText: error.response?.statusText,
              data: error.response?.data,
              config: error.config,
              fullResponse: error.response
            })
            console.warn('⚠️ SMS failed, but booking will continue. User will not receive SMS confirmation.')
            // Don't throw error - allow booking to succeed without SMS
          }
        }

        if (data.email) {
          try {
            console.log('📧 Sending Email...')

            const emailPayload = {
              from: { email: 'noreply@meta-health.co.za' },
              personalizations: [
                {
                  to: [{ email: data.email }],
                  dynamic_template_data: {
                    firstName: data.firstName,
                    otp,
                    bookingDate: dayjs(data.bookingDate).format('DD MMM YYYY'),
                    startTime: data.startTime || 'Time to be confirmed',
                    medicalProfName: `${data.medicalProf.firstName} ${data.medicalProf.lastName}`
                  }
                }
              ],
              template_id: 'd-ad0301d8918e484baa16ddaf4d57eed0'
            }

            console.log('📧 Email payload:', JSON.stringify(emailPayload, null, 2))

            await agilite.Connectors.execute('sendgrid_new', 'send_dynamic', {
              apiKey: process.env.REACT_APP_SENDGRID_API_KEY,
              data: JSON.stringify(emailPayload)
            })
            console.log('✅ Email sent successfully')
          } catch (error) {
            console.error('❌ Email failed:', error)
            console.error('❌ Email error details:', {
              message: error.message,
              status: error.response?.status,
              statusText: error.response?.statusText,
              data: error.response?.data,
              fullErrorResponse: JSON.stringify(error.response, null, 2)
            })
            console.warn('📧 Email failed but continuing with SMS-only notification')
            // Don't throw error - allow booking to succeed with SMS notification only
          }
        }

        resolve(otp)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

export const getBookings = async (state, qry, userRef, queryOptions = {}) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      let filter = null
      let tmpBookings = []
      let medProfIds = []
      let patientIds = []
      let tmpMedicalProfessionals = []
      let tmpPatients = []

      try {
        if (!state.auth.completeEmailSetup && state.auth.agiliteUser) {
          filter = { userRef: userRef ? userRef : state.auth.agiliteUser._id, ...qry }

          tmpBookings = await readBookings(filter, queryOptions)
          medProfIds = tmpBookings.map((booking) => booking.medicalProfRef)
          patientIds = tmpBookings.map((booking) => booking.userRef)
          tmpMedicalProfessionals = await readMedicalProfessionalUsers({
            _id: { $in: medProfIds }
          })
          tmpPatients = await readPatients({ _id: { $in: patientIds } })

          for (const booking of tmpBookings) {
            for (const medicalProf of tmpMedicalProfessionals) {
              if (booking.medicalProfRef === medicalProf._id) {
                booking.medicalProfName = medicalProf.firstName + ' ' + medicalProf.lastName
                booking.medicalProfImage = medicalProf.profileImage
              }
            }

            for (const patient of tmpPatients) {
              if (booking.userRef === patient._id) {
                booking.patientName = patient.firstName + ' ' + patient.lastName
              }
            }

            for (const clinic of state.clinics.data) {
              if (clinic._id === booking.clinicRef) {
                booking.clinicName = clinic.name
              }
            }

            for (const service of state.services.data) {
              if (service._id === booking.service) {
                booking.serviceName = service.name
                booking.serviceType = service.type
              }
            }

            for (const virtualService of state.virtualServices.data) {
              if (virtualService._id === booking.service) {
                booking.serviceName = virtualService.name
              }
            }
          }
        }

        resolve(tmpBookings)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

export const getAppointments = async (state, qry) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      const userIds = []
      const professionalIds = []
      let filter = null
      let tmpAppointments = []
      let tmpPatients = []
      let tmpMedicalProfessionals = []
      let clinics = []
      let services = []
      let virtualServices = []

      try {
        if (!state.auth.completeEmailSetup && state.auth.agiliteUser) {
          if (
            state.auth.agiliteUser.extraData.profession === 'nurse' ||
            state.auth.agiliteUser.extraData.role.type === 'reception' ||
            state.auth.agiliteUser.extraData.role.isManager ||
            state.auth.agiliteUser.extraData.role.type === 'admin'
          ) {
            filter = { ...qry }
          } else {
            // medicalProfRef: state.auth.agiliteUser._id
            filter = { ...qry }
          }

          tmpAppointments = await readBookings(filter)
          clinics = await readClinics()
          services = await readServices()
          virtualServices = await readVirtualServices()

          for (const booking of tmpAppointments) {
            if (booking.userRef) userIds.push(booking.userRef)
            if (booking.medicalProfRef) professionalIds.push(booking.medicalProfRef)
          }

          tmpPatients = await readSystemUsersAggregate({ _id: { $in: userIds } }, null, null, null, true)
          tmpMedicalProfessionals = await readMedicalProfessionalUsers({
            _id: { $in: professionalIds }
          })

          for (const appointment of tmpAppointments) {
            for (const patient of tmpPatients) {
              if (appointment.userRef === patient._id) {
                appointment.patientName = patient.firstName + ' ' + patient.lastName
                appointment.patientRecord = patient
              }

              for (const clinic of clinics) {
                if (clinic._id === appointment.clinicRef) {
                  appointment.clinicName = clinic.name
                  appointment.clinicRecord = clinic
                }
              }
              const bookingService =
                services.find((i) => i._id === appointment.service) ||
                virtualServices.find((i) => i._id === appointment.service)
              appointment.serviceName = bookingService?.name ? bookingService?.name : 'Could Not Find Service'
              appointment.serviceType = bookingService?.type ? bookingService?.type : 'Could Not Find Service'
              appointment.serviceRecord = bookingService
            }

            for (const medicalProf of tmpMedicalProfessionals) {
              if (appointment.medicalProfRef === medicalProf._id) {
                appointment.medicalProfName = medicalProf.firstName + ' ' + medicalProf.lastName
              }
            }
          }
        }

        resolve(tmpAppointments)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

export const generateAgoraRtcToken = async (bookingId, userRef) => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      let response = null

      try {
        response = await axios.get(`${process.env.REACT_APP_NODE_RED_URL}/api/agora/rtcToken`, {
          headers: { 'channel-name': bookingId, 'user-ref': userRef }
        })
        resolve(response.data)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

export const getConsultationTypes = () => {
  return new Promise((resolve, reject) => {
    let response = null

    ;(async () => {
      try {
        response = await agilite.Keywords.getValuesByProfileKey('consultation_types')
        resolve(response.data)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

export const readBillingRecords = (qry) => {
  return new Promise((resolve, reject) => {
    let response = null

    ;(async () => {
      try {
        response = await agilite.Connectors.execute('billing_records', 'read', { filter: JSON.stringify(qry) })
        resolve(response.data)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

export const createBillingRecord = (data) => {
  return new Promise((resolve, reject) => {
    let response = null

    ;(async () => {
      try {
        response = await agilite.Connectors.execute('billing_records', 'create', { data: JSON.stringify(data) })
        resolve(response.data)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

export const countBillingRecords = (filter) => {
  return new Promise((resolve, reject) => {
    let response = null

    ;(async () => {
      try {
        response = await agilite.Connectors.execute('billing_records', 'count', { filter: JSON.stringify(filter) })
        resolve(response.data)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

export const updateBillingRecord = (recordId, data) => {
  return new Promise((resolve, reject) => {
    let response = null

    ;(async () => {
      try {
        response = await agilite.Connectors.execute('billing_records', 'update', {
          recordId,
          data: JSON.stringify(data)
        })
        resolve(response.data)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

export const deleteBillingRecord = (recordId) => {
  return new Promise((resolve, reject) => {
    let response = null

    ;(async () => {
      try {
        response = await agilite.Connectors.execute('billing_records', 'delete', { recordId })
        resolve(response.data)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

export const getProductPrice = (productType, productCode, planOptionCode) => {
  return new Promise((resolve, reject) => {
    let response = null

    ;(async () => {
      try {
        response = await axios.post(
          `${process.env.REACT_APP_NODE_RED_URL}/api/medprax/product_pricing`,
          {},
          {
            headers: {
              'product-type': productType,
              'product-code': productCode,
              'plan-option-code': planOptionCode
            }
          }
        )
        resolve(response.data)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

export const getProcedurePrice = (productCode, planOptionCode, isContract, billingProfile) => {
  return new Promise((resolve, reject) => {
    let body = null
    let response = null
    let defaultDisciplineCode = '014'

    ;(async () => {
      try {
        body = {
          disciplineCode: billingProfile.disciplinePrefix || defaultDisciplineCode,
          isContract,
          isNetwork: billingProfile.network,
          isIpa: billingProfile.ipa,
          planOptionCode,
          tariffCode: productCode
        }

        response = await axios.post(`${process.env.REACT_APP_NODE_RED_URL}/api/medprax/tariff_pricing`, body)
        resolve(response.data)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

export const getPlanSubOptions = (planOptionCode) => {
  return new Promise((resolve, reject) => {
    let response = null

    ;(async () => {
      try {
        response = await axios.get(`${process.env.REACT_APP_NODE_RED_URL}/api/medprax/scheme_plan_sub_options`, {
          headers: {
            'plan-option-code': planOptionCode
          }
        })
        resolve(response.data)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

const generateSystemReferenceNumber = (index) => {
  // Get current timestamp in milliseconds and convert to string
  const timestamp = Date.now().toString()

  // Take last 8 digits for invoice number
  const invoiceNumber = timestamp.slice(-8)

  // Generate 7 digit line number where last 6 must be numeric
  // Use index padded to 6 digits for uniqueness per line
  const lineNumber = `1${index.toString().padStart(6, '0')}`

  return `${invoiceNumber}/${lineNumber}`
}

const generateGoodXLineItems = (billingItems, bookingDate) => {
  return billingItems.map((item, index) => {
    let type = ''
    let id = index + 1

    switch (item.type) {
      case 'Procedure':
        type = 'ClaimLineTariffItem'
        break
      case 'Material':
      case 'Medicine':
        type = 'ClaimLineProductItem'
        break
      default:
        type = 'ClaimLineTariffItem'
        break
    }

    if (type === 'ClaimLineTariffItem') {
      return {
        $: {
          id: id
        },
        ClaimLineTariffItem: [
          {
            CommonClaimLineItemInfo: [
              {
                BillingPracticeLineItemId: [id],
                TradingPartyLineItemId: [item._id],
                SystemReferenceNumber: [generateSystemReferenceNumber(id)],
                TreatingProviderId: ['1'],
                TreatingDiagnosisId: ['1'],
                PatientId: ['1'],
                LaboratoryRequisitionNumber: [''],
                DateTimeOfTreatment: [new Date(bookingDate).toISOString()],
                EndDateTimeOfTreatment: [new Date(bookingDate).toISOString()],
                Quantity: [item.qty],
                QuantityType: ['6'],
                PlaceOfService: ['11'],
                ServiceLocation: [''],
                AuthorisationNumber: [''],
                DescriptionOfItem: [item.name],
                CommonClaimCode: [''],
                SystemItemCode: [''],
                ClaimItemFinancial: [
                  {
                    CommonClaimBaseFinancialInfo: [
                      {
                        SubmittedGrossAmount: [item.medicalAid],
                        SubmittedClaimAmount: [item.medicalAid]
                      }
                    ],
                    SubmittedBaseCost: [
                      {
                        _: item.medicalAid,
                        $: {
                          pricingTariffIndicator: ''
                        }
                      }
                    ],
                    SubmittedLineItemCollections: ['0.00']
                  }
                ]
              }
            ],
            ProcedureCode: [
              {
                _: item.code,
                $: {
                  proceduralCodingStandardIdentifier: 'NHRPL'
                }
              }
            ],
            CodeType: ['0'],
            GXUnitType: ['2']
          }
        ]
      }
    } else {
      return {
        $: {
          id: id
        },
        ClaimLineProductItem: [
          {
            CommonClaimLineItemInfo: [
              {
                BillingPracticeLineItemId: [id],
                TradingPartyLineItemId: [item._id],
                SystemReferenceNumber: [generateSystemReferenceNumber(id)],
                TreatingProviderId: ['1'],
                TreatingDiagnosisId: ['1'],
                PatientId: ['1'],
                LaboratoryRequisitionNumber: [''],
                DateTimeOfTreatment: [new Date(bookingDate).toISOString()],
                EndDateTimeOfTreatment: [new Date(bookingDate).toISOString()],
                Quantity: [item.qty],
                QuantityType: ['6'],
                PlaceOfService: [''],
                ServiceLocation: ['11'],
                AuthorisationNumber: [''],
                DescriptionOfItem: [item.name],
                CommonClaimCode: [''],
                SystemItemCode: [''],
                ClaimItemFinancial: [
                  {
                    CommonClaimBaseFinancialInfo: [
                      {
                        SubmittedGrossAmount: [item.medicalAid],
                        SubmittedClaimAmount: [item.medicalAid]
                      }
                    ],
                    SubmittedBaseCost: [
                      {
                        _: item.medicalAid,
                        $: {
                          pricingTariffIndicator: ''
                        }
                      }
                    ],
                    SubmittedLineItemCollections: ['0.00']
                  }
                ]
              }
            ],
            ProductCode: [
              {
                _: item.nappiCode,
                $: {
                  type: 'NAPPI'
                }
              }
            ],
            DailyDosage: [''],
            DaysOfTherapy: [
              {
                _: '0',
                $: {
                  basisOfDaysOfTherapy: '0'
                }
              }
            ],
            ProductType: ['2']
          }
        ]
      }
    }
  })
}

export const goodXSubmit = (data) => {
  return new Promise((resolve, reject) => {
    let response = null

    ;(async () => {
      try {
        console.log('=== GoodX Submit Debug ===')
        console.log('Clinic Record disciplinePrefix:', data.clinicRecord.disciplinePrefix)
        console.log('Clinic Record:', data.clinicRecord)

        // Import WCA utilities
        const { isWCAClaim, getWCAPayorInfo, generateWCAInfoSection, validateWCAClaimData } = await import(
          '../../Invoices/utils/wca-claims'
        )

        // Check if this is a WCA claim
        console.log('🔍 [WCA-DEBUG] === GoodX Submit WCA Analysis ===')
        console.log('🔍 [WCA-DEBUG] Full data object keys:', Object.keys(data))
        console.log('🔍 [WCA-DEBUG] Invoice data summary:')
        console.log('  - _id:', data._id)
        console.log('  - invoiceType:', data.invoiceType)
        console.log('  - claimType:', data.claimType)
        console.log('  - isWCAclaim:', data.isWCAclaim)
        console.log('  - patientRecord exists:', !!data.patientRecord)
        console.log('  - medicalAid exists:', !!data.patientRecord?.medicalAid)

        if (data.patientRecord?.medicalAid) {
          console.log('🔍 [WCA-DEBUG] Patient medical aid details:')
          console.log('  - name:', data.patientRecord.medicalAid.name)
          console.log('  - plan:', data.patientRecord.medicalAid.plan)
          console.log('  - schemeCode:', data.patientRecord.medicalAid.schemeCode)
          console.log('  - number:', data.patientRecord.medicalAid.number)
        }

        const isWCA = isWCAClaim(data)
        console.log('🔍 [WCA-DEBUG] Final WCA determination:', isWCA)

        // Validate WCA data if it's a WCA claim
        if (isWCA) {
          console.log('🔍 [WCA-DEBUG] Validating WCA claim data...')
          const validation = validateWCAClaimData(data)
          console.log('🔍 [WCA-DEBUG] Validation result:', validation)
          if (!validation.isValid) {
            throw new Error(`WCA Claim Validation Failed: ${validation.errors.join(', ')}`)
          }
        }

        // Get payor information (WCA-specific or regular medical aid)
        console.log('🔍 [WCA-DEBUG] Getting payor information...')
        const payorInfo = isWCA
          ? getWCAPayorInfo(data.patientRecord.medicalAid)
          : {
              ClaimOption: data.patientRecord.medicalAid.plan,
              OptionOrPlanReference: data.planSubOption.code,
              Name: data.patientRecord.medicalAid.name
            }

        console.log('🔍 [WCA-DEBUG] Payor info:', payorInfo)

        // Generate WCAInfo section if it's a WCA claim
        let wcaInfoSection = null
        if (isWCA) {
          wcaInfoSection = generateWCAInfoSection(data)
          console.log('🔍 [WCA-DEBUG] Generated WCAInfo section:', wcaInfoSection)
        }

        const claimRequest = {
          ProviderClaimBatchEnvelope: {
            ProviderClaimMessageEnvelope: [
              {
                CommonMessageEnvelopeInfo: [
                  {
                    SystemInformation: [
                      {
                        SoftwareTitle: ['Meta-Clinic Portal'],
                        SoftwareVersion: [process.env.REACT_APP_VERSION]
                      }
                    ],
                    Payor: [
                      {
                        ClaimOption: [payorInfo.ClaimOption],
                        OptionOrPlanReference: [payorInfo.OptionOrPlanReference],
                        Name: [payorInfo.Name]
                      }
                    ]
                  }
                ],
                OriginalDocumentNumber: [data._id],
                ClaimTransactionType: ['PHISC01'],
                ProviderClaim: [
                  {
                    DispensingProviderClaim: [
                      {
                        CommonClaimHeaderInfo: [
                          {
                            ClaimNumber: [data._id],
                            ClaimTransactionType: ['PHISC01'],
                            DateTransactionCreated: [new Date().toISOString()],
                            BillingPractice: [
                              {
                                PCNSNumber: [data.clinicRecord.practiceNumber],
                                SpecialityCode: [
                                  (() => {
                                    const value = data.clinicRecord.disciplinePrefix || '014'
                                    console.log('BillingPractice SpecialityCode:', value, 'Length:', value.length)
                                    return value
                                  })()
                                ],
                                VATRegistrationNumber: [''],
                                AccountNumber: [''],
                                Name: [data.clinicRecord.name],
                                ContactInformation: [
                                  {
                                    AddressLineOne: [data.clinicRecord.physicalAddress],
                                    AddressLineTwo: [''],
                                    AddressLineThree: [''],
                                    AddressLineFour: [''],
                                    AddressLineFive: [''],
                                    TelephoneHome: [data.clinicRecord.phoneNumber],
                                    TelephoneWork: [data.clinicRecord.phoneNumber]
                                  }
                                ],
                                TreatingProvider: [
                                  {
                                    $: {
                                      id: '1'
                                    },
                                    CommonProviderInfo: [
                                      {
                                        RegistrationNumber: [
                                          {
                                            _: '',
                                            $: {
                                              typeOfBody: 'HPCSA'
                                            }
                                          }
                                        ],
                                        PCNSNumber: [data.clinicRecord.practiceNumber],
                                        SpecialityCode: [
                                          (() => {
                                            const value = data.clinicRecord.disciplinePrefix || '014'
                                            console.log(
                                              'TreatingProvider SpecialityCode:',
                                              value,
                                              'Length:',
                                              value.length
                                            )
                                            return value
                                          })()
                                        ]
                                      }
                                    ],
                                    DispensingLicenseNumber: [''],
                                    FullName: [
                                      {
                                        Title: ['DR'],
                                        Initials: [data.medicalProf.firstName[0]],
                                        Surname: [data.medicalProf.lastName]
                                      }
                                    ]
                                  }
                                ]
                              }
                            ],
                            Policy: [
                              {
                                PolicyNumber: [data.mainMemberRecord.medicalAid.number],
                                PolicyOwner: [
                                  {
                                    CommonPersonInfo: [
                                      {
                                        Initials: [data.mainMemberRecord.firstName[0]],
                                        Surname: [data.mainMemberRecord.lastName],
                                        IdentityNumber: [data.mainMemberRecord.idNo]
                                      }
                                    ],
                                    Title: [generateTitle(data.mainMemberRecord.gender)],
                                    ContactInformation: [
                                      {
                                        AddressLineOne: [data.mainMemberRecord.residentialAddress],
                                        AddressLineTwo: [''],
                                        AddressLineThree: [''],
                                        AddressLineFour: [''],
                                        AddressLineFive: [''],
                                        TelephoneHome: [''],
                                        TelephoneWork: [''],
                                        TelephoneMobile: [data.mainMemberRecord.phoneNumber],
                                        Email: [data.mainMemberRecord.email]
                                      }
                                    ]
                                  }
                                ],
                                Patient: [
                                  {
                                    $: {
                                      id: '1'
                                    },
                                    CommonPersonInfo: [
                                      {
                                        Initials: [data.patientRecord.firstName[0]],
                                        Surname: [data.patientRecord.lastName],
                                        IdentityNumber: [data.patientRecord.idNo]
                                      }
                                    ],
                                    FirstName: [data.patientRecord.firstName],
                                    DependantCode: [data.patientRecord.medicalAid.dependantNumber],
                                    DateOfBirth: [data.patientRecord.dateOfBirth],
                                    Gender: [data.patientRecord.gender],
                                    ContactInformation: [
                                      {
                                        AddressLineOne: [data.patientRecord.residentialAddress],
                                        AddressLineTwo: [''],
                                        AddressLineThree: [''],
                                        AddressLineFour: [''],
                                        AddressLineFive: [''],
                                        TelephoneHome: [''],
                                        TelephoneWork: [''],
                                        TelephoneMobile: [data.patientRecord.phoneNumber],
                                        Email: [data.patientRecord.email]
                                      }
                                    ]
                                  }
                                ],
                                BenefitCheckCode: [''],
                                BenefitCheckAmount: [
                                  data.billingItems
                                    .reduce((acc, item) => acc + parseFloat(item.medicalAid), 0)
                                    .toFixed(2)
                                ]
                              }
                            ],
                            // Add WCAInfo section for WCA claims
                            ...(isWCA && wcaInfoSection ? { WCAInfo: [wcaInfoSection] } : {}),
                            Payor: [
                              {
                                ClaimOption: [payorInfo.ClaimOption],
                                OptionOrPlanReference: [payorInfo.OptionOrPlanReference],
                                Name: [payorInfo.Name]
                              }
                            ],
                            ClaimHeaderFinancial: [
                              {
                                CommonClaimBaseFinancialInfo: [
                                  {
                                    SubmittedGrossAmount: [
                                      data.billingItems
                                        .reduce((acc, item) => acc + parseFloat(item.medicalAid), 0)
                                        .toFixed(2)
                                    ],
                                    SubmittedClaimAmount: [
                                      data.billingItems
                                        .reduce((acc, item) => acc + parseFloat(item.medicalAid), 0)
                                        .toFixed(2)
                                    ]
                                  }
                                ],
                                SubmittedClinicalEncounterCollections: ['0.00']
                              }
                            ],
                            Diagnosis:
                              data.billingItems.length > 0
                                ? data.billingItems.map((item, index) => {
                                    return {
                                      $: {
                                        id: index + 1
                                      },
                                      DiagnosisCode: [
                                        {
                                          _: item.icd10.length > 0 ? item.icd10[0] : 'Z76.9',
                                          $: {
                                            sequence: index + 1
                                          }
                                        }
                                      ]
                                    }
                                  })
                                : [
                                    {
                                      $: {
                                        id: 1
                                      },
                                      DiagnosisCode: [
                                        {
                                          _: 'Z76.9',
                                          $: {
                                            sequence: 1
                                          }
                                        }
                                      ]
                                    }
                                  ]
                          }
                        ],
                        ClaimLineItem: generateGoodXLineItems(data.billingItems, data.bookingDate)
                      }
                    ]
                  }
                ]
              }
            ],
            ClaimBatchTrackingInformation: [
              {
                CommonBatchTrackingInformation: [
                  {
                    TrackingInformation: [
                      {
                        SendingTradingPartyBatchNumber: ['']
                      }
                    ]
                  }
                ]
              }
            ]
          }
        }

        console.log(
          `🚀 [GOODX-SUBMIT] Sending request to: ${process.env.REACT_APP_NODE_RED_URL}/api/goodx/createTicket`
        )
        console.log('🚀 [GOODX-SUBMIT] Full claim request:', JSON.stringify(claimRequest, null, 2))

        // WCA-specific debugging
        if (isWCA) {
          console.log('🔍 [WCA-DEBUG] === WCA Claim Final Check ===')
          console.log('🔍 [WCA-DEBUG] This is confirmed as a WCA claim')
          console.log(
            '🔍 [WCA-DEBUG] WCAInfo in request:',
            claimRequest.ProviderClaimBatchEnvelope?.ProviderClaimMessageEnvelope?.[0]?.DispensingProviderClaim?.[0]
              ?.WCAInfo
          )
          console.log(
            '🔍 [WCA-DEBUG] Payor in request:',
            claimRequest.ProviderClaimBatchEnvelope?.ProviderClaimMessageEnvelope?.[0]?.DispensingProviderClaim?.[0]
              ?.Payor
          )
        }

        response = await axios.post(`${process.env.REACT_APP_NODE_RED_URL}/api/goodx/createTicket`, claimRequest, {
          headers: {
            'Content-Type': 'application/json'
          }
        })

        console.log('📥 [GOODX-SUBMIT] Response received:', {
          status: response.status,
          statusText: response.statusText,
          headers: response.headers,
          dataType: typeof response.data,
          data: response.data
        })

        // WCA-specific response debugging
        if (isWCA) {
          console.log('🔍 [WCA-DEBUG] === WCA Response Analysis ===')
          console.log('🔍 [WCA-DEBUG] Full response data:', response.data)
          if (typeof response.data === 'string') {
            console.log('🔍 [WCA-DEBUG] Response contains "private":', response.data.toLowerCase().includes('private'))
            console.log('🔍 [WCA-DEBUG] Response contains "WCA":', response.data.includes('WCA'))
            console.log('🔍 [WCA-DEBUG] Response contains "WORKMANS":', response.data.includes('WORKMANS'))
          }
        }

        resolve(response.data)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

export const goodXStatus = async (ticketNo) => {
  try {
    console.log(`🔍 [GOODX-STATUS] Checking GoodX status for ticket: ${ticketNo}`)
    console.log(`🔍 [GOODX-STATUS] Request URL: ${process.env.REACT_APP_NODE_RED_URL}/api/goodx/status`)
    console.log('🔍 [GOODX-STATUS] Request Headers:', { 'ticket-no': ticketNo })

    const response = await axios.get(`${process.env.REACT_APP_NODE_RED_URL}/api/goodx/status`, {
      headers: {
        'ticket-no': ticketNo
      }
    })

    console.log(`📥 [GOODX-STATUS] Raw response for ticket ${ticketNo}:`, {
      status: response.status,
      statusText: response.statusText,
      headers: response.headers,
      dataType: typeof response.data,
      dataLength: typeof response.data === 'string' ? response.data.length : 'N/A',
      isXML: typeof response.data === 'string' && response.data.trim().startsWith('<?xml'),
      firstChars: typeof response.data === 'string' ? response.data.substring(0, 200) : 'N/A',
      lastChars:
        typeof response.data === 'string' ? response.data.substring(Math.max(0, response.data.length - 100)) : 'N/A'
    })

    console.log(`📥 [GOODX-STATUS] Full response data for ${ticketNo}:`, response.data)

    return response.data
  } catch (e) {
    console.error(`❌ [GOODX-STATUS] Status check failed for ticket ${ticketNo}:`, {
      error: e.message,
      status: e.response?.status,
      statusText: e.response?.statusText,
      responseHeaders: e.response?.headers,
      responseData: e.response?.data,
      stack: e.stack
    })
    throw e
  }
}

export const goodXResponse = (ticketNo) => {
  return new Promise((resolve, reject) => {
    let response = null

    ;(async () => {
      try {
        console.log(`🔍 [GOODX-RESPONSE] Fetching detailed response for ticket: ${ticketNo}`)
        console.log(`🔍 [GOODX-RESPONSE] Request URL: ${process.env.REACT_APP_NODE_RED_URL}/api/goodx/response`)
        console.log('🔍 [GOODX-RESPONSE] Request Headers:', { 'ticket-no': ticketNo })

        // Wait 2 seconds first
        await new Promise((resolve) => setTimeout(resolve, 2000))
        response = await axios.get(`${process.env.REACT_APP_NODE_RED_URL}/api/goodx/response`, {
          headers: {
            'ticket-no': ticketNo
          }
        })

        console.log('📥 [GOODX-RESPONSE] Response received:', {
          status: response.status,
          statusText: response.statusText,
          headers: response.headers,
          dataType: typeof response.data,
          dataLength: typeof response.data === 'string' ? response.data.length : 'N/A',
          data: response.data
        })

        resolve(response.data)
      } catch (e) {
        console.error(`❌ [GOODX-RESPONSE] Failed to fetch response for ticket ${ticketNo}:`, {
          error: e.message,
          status: e.response?.status,
          statusText: e.response?.statusText,
          responseHeaders: e.response?.headers,
          responseData: e.response?.data
        })
        reject(e)
      }
    })()
  })
}

export const goodXBenefitCheck = (bookingData, patient, clinic, amount, planSubOption) => {
  return new Promise((resolve, reject) => {
    const claimRequest = {
      ProviderClaimBatchEnvelope: {
        ProviderClaimMessageEnvelope: [
          {
            CommonMessageEnvelopeInfo: [
              {
                SystemInformation: [
                  {
                    SoftwareTitle: ['Meta-Clinic Portal'],
                    SoftwareVersion: [process.env.REACT_APP_VERSION]
                  }
                ],
                Payor: [
                  {
                    ClaimOption: [patient.medicalAid.planOption],
                    OptionOrPlanReference: [planSubOption.code],
                    Name: [patient.medicalAid.name]
                  }
                ]
              }
            ],
            OriginalDocumentNumber: [patient._id],
            ClaimTransactionType: ['GDX02'],
            MemberValidation: [
              {
                CommonClaimHeaderInfo: [
                  {
                    ClaimNumber: [bookingData._id],
                    ClaimTransactionType: ['GDX02'],
                    DateTransactionCreated: [new Date().toISOString()],
                    BillingPractice: [
                      {
                        PCNSNumber: [clinic.practiceNumber],
                        SpecialityCode: [
                          (() => {
                            const value = clinic.disciplinePrefix || '014'
                            console.log('BenefitCheck SpecialityCode:', value, 'Length:', value.length)
                            return value
                          })()
                        ],
                        VATRegistrationNumber: [''],
                        AccountNumber: [''],
                        Name: [clinic.name],
                        ContactInformation: [
                          {
                            AddressLineOne: [clinic.physicalAddress],
                            AddressLineTwo: [''],
                            AddressLineThree: [''],
                            AddressLineFour: [''],
                            AddressLineFive: [''],
                            TelephoneHome: [clinic.phoneNumber],
                            TelephoneWork: [clinic.phoneNumber]
                          }
                        ]
                      }
                    ],
                    Policy: [
                      {
                        PolicyNumber: [patient.medicalAid.number],
                        BenefitCheckCode: [''],
                        BenefitCheckAmount: [amount.toFixed(2)]
                      }
                    ],
                    Payor: [
                      {
                        ClaimOption: [patient.medicalAid.planOption],
                        OptionOrPlanReference: [planSubOption.code],
                        Name: [patient.medicalAid.name]
                      }
                    ]
                  }
                ]
              }
            ]
          }
        ],
        ClaimBatchTrackingInformation: [
          {
            CommonBatchTrackingInformation: [
              {
                TrackingInformation: [
                  {
                    SendingTradingPartyBatchNumber: ['']
                  }
                ]
              }
            ]
          }
        ]
      }
    }

    ;(async () => {
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_NODE_RED_URL}/api/goodx/createTicket`,
          claimRequest,
          {
            headers: {
              'Content-Type': 'application/json'
            }
          }
        )

        resolve(response.data)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

export const testAggregateBookings = async (pipeline) => {
  try {
    // Make the actual request
    const response = await fetch('/api/bookings/aggregate', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('token')}`
      },
      body: JSON.stringify({ pipeline })
    })

    const data = await response.json()

    return data
  } catch (error) {
    console.error('Error in test aggregate:', error)
    throw error
  }
}
